// 类型时间线（SVG 手画）：横轴时间，运行为圆点（绿=完成 红=失败 灰=取消 橙=卡住，dry run 空心），叠加变更标记竖线
import { useEffect, useMemo, useRef, useState } from "react";
import type { MarkerItem, TimelineRun } from "../lib/types";
import { fmtDateTime } from "../lib/format";

export const RUN_COLORS: Record<string, string> = {
  done: "#188038",
  failed: "#d93025",
  cancelled: "#80868b",
  blocked: "#f29900",
  running: "#1a73e8",
  waiting_scan: "#f29900",
  review_required: "#9334e6",
};

export const MARKER_COLORS: Record<string, string> = {
  skill: "#1a73e8",
  rollback: "#d93025",
  "agents-md": "#9334e6",
  model: "#e37400",
  openclaw: "#12a4af",
  portal: "#c5221f",
  maturity: "#188038",
};

export const MARKER_TEXT: Record<string, string> = {
  skill: "SOP 改动",
  rollback: "回滚",
  "agents-md": "AGENTS.md",
  model: "模型变化",
  openclaw: "OpenClaw 版本",
  portal: "门户改版",
  maturity: "成熟度",
};

const RUN_TEXT: Record<string, string> = { done: "完成", failed: "失败", cancelled: "取消", blocked: "卡住", running: "执行中" };

const H = 190;
const PAD_L = 16;
const PAD_R = 16;
const AXIS_Y = 160;

interface Hover {
  x: number;
  y: number;
  text: string;
}

/** 时间 → x 坐标（纯函数，便于测试） */
export function makeScale(t0: number, t1: number, width: number): (t: number) => number {
  const span = Math.max(1, t1 - t0);
  const w = Math.max(1, width - PAD_L - PAD_R);
  return (t: number) => PAD_L + ((t - t0) / span) * w;
}

export function TimelineChart({
  runs,
  markers,
  days,
  onRunClick,
}: {
  runs: TimelineRun[];
  markers: MarkerItem[];
  days: number;
  onRunClick: (r: TimelineRun) => void;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  const [hover, setHover] = useState<Hover | null>(null);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const update = () => setWidth(Math.max(320, el.clientWidth));
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { t0, t1 } = useMemo(() => {
    const times = [...runs.map((r) => +new Date(r.at)), ...markers.map((m) => +new Date(m.at))];
    const now = Date.now();
    const min = times.length ? Math.min(...times) : now - days * 86_400_000;
    const max = Math.max(now, ...times);
    const pad = Math.max(3_600_000, (max - min) * 0.03);
    return { t0: min - pad, t1: max + pad };
  }, [runs, markers, days]);
  const x = makeScale(t0, t1, width);

  const ticks = useMemo(() => {
    const out: number[] = [];
    const n = Math.max(2, Math.floor(width / 140));
    for (let i = 0; i <= n; i++) out.push(t0 + ((t1 - t0) * i) / n);
    return out;
  }, [t0, t1, width]);

  return (
    <div className="chart-wrap" ref={wrap} style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${width} ${H}`} height={H} role="img" aria-label="运行时间线" data-testid="timeline-chart">
        <line x1={PAD_L} x2={width - PAD_R} y1={AXIS_Y} y2={AXIS_Y} stroke="currentColor" opacity={0.25} />
        {ticks.map((t) => (
          <text key={t} x={x(t)} y={AXIS_Y + 18} fontSize={11} textAnchor="middle" fill="currentColor" opacity={0.6}>
            {new Date(t).toLocaleDateString("zh-CN", { month: "numeric", day: "numeric" })}
          </text>
        ))}
        {markers.map((m) => {
          const mx = x(+new Date(m.at));
          const c = MARKER_COLORS[m.kind] ?? "#888";
          const text = `${MARKER_TEXT[m.kind] ?? m.kind}：${m.text}（${fmtDateTime(m.at)}）`;
          return (
            <g key={m.id} onMouseEnter={() => setHover({ x: mx, y: 14, text })} onMouseLeave={() => setHover(null)} data-testid="marker">
              <line x1={mx} x2={mx} y1={8} y2={AXIS_Y} stroke={c} strokeWidth={2} strokeDasharray="4 3" />
              <rect x={mx - 6} y={4} width={12} height={10} fill={c} rx={2} />
              <rect x={mx - 6} y={4} width={12} height={AXIS_Y - 4} fill="transparent" />
              <title>{text}</title>
            </g>
          );
        })}
        {runs.map((r, i) => {
          const cx = x(+new Date(r.at));
          const cy = 60 + (i % 4) * 22;
          const c = RUN_COLORS[r.status] ?? "#1a73e8";
          const text = `${RUN_TEXT[r.status] ?? r.status}${r.dryRun ? "（dry run）" : ""} · ${fmtDateTime(r.at)}${r.minutes ? ` · ${r.minutes} 分钟` : ""}${r.skillVersion ? ` · SOP v${r.skillVersion}` : ""}${r.sigChanged ? " · 门户签名变化" : ""}${r.unexpected ? ` · 意外干预 ${r.unexpected} 次` : ""}`;
          return (
            <circle
              key={r.id}
              cx={cx}
              cy={cy}
              r={7}
              fill={r.dryRun ? "transparent" : c}
              stroke={c}
              strokeWidth={r.dryRun ? 2.5 : 1}
              style={{ cursor: "pointer" }}
              onClick={() => onRunClick(r)}
              onMouseEnter={() => setHover({ x: cx, y: cy, text })}
              onMouseLeave={() => setHover(null)}
              data-testid="run-dot"
              data-status={r.status}
            >
              <title>{text}</title>
            </circle>
          );
        })}
      </svg>
      {hover && (
        <div
          style={{
            position: "absolute",
            left: Math.min(Math.max(0, hover.x - 120), width - 260),
            top: hover.y + 14,
            width: 260,
            background: "var(--panel)",
            border: "1px solid var(--line)",
            borderRadius: 8,
            padding: "6px 10px",
            fontSize: 13,
            boxShadow: "var(--shadow)",
            pointerEvents: "none",
          }}
        >
          {hover.text}
        </div>
      )}
      <div className="legend" style={{ marginTop: 6 }}>
        {["done", "failed", "cancelled", "blocked"].map((s) => (
          <span key={s}>
            <i style={{ background: RUN_COLORS[s] }} />
            {RUN_TEXT[s]}
          </span>
        ))}
        <span>
          <i style={{ border: "2px solid #888", background: "transparent" }} />
          dry run
        </span>
        {Object.entries(MARKER_TEXT).map(([k, t]) => (
          <span key={k}>
            <i style={{ background: MARKER_COLORS[k], borderRadius: 2 }} />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}
